<template>
  <div class="calendar-root" @click="elementClicked">
    <ButtonWithIcon class="input-date" :class="{ active: isOpen }" :clicked="toggleCalendar">
      <template slot="buttonTitle">
        {{ DateLabel }}
      </template>
      <i slot="rightIcon" class="icon-angle-down" />
    </ButtonWithIcon>
    <div v-if="isOpen" class="calendar calendar-right-to-left">
      <div class="calendar-wrap">
        <div v-if="showMonth" class="calendar_month_left">
          <div class="months-text">
            <button class="is-pulled-left button white-button" :disabled="isBefore2019" @click="goPrevMonth">
              <i class="icon-angle-left" aria-hidden="true" />
            </button>
            <span class="month-title">{{ monthsLocale[activeMonthStart] | t }}&nbsp;{{ activeYearStart }}</span>
          </div>
          <ul :class="s.daysWeeks">
            <li v-for="item in shortDaysLocale" :key="item">
              {{ item | t }}
            </li>
          </ul>
          <ul v-for="r in 6" :key="r" class="calendar-body" :class="[s.days]">
            <!-- eslint-disable vue/no-v-html -->
            <li
              v-for="i in numOfDays"
              :key="i"
              :class="[
                {
                  [s.daysSelected]: isDateSelected(r, i, 'first', startMonthDay, endMonthDate),
                  [s.daysInRange]: isDateInRange(r, i, 'first', startMonthDay, endMonthDate),
                  [s.dateDisabled]: isDateDisabled(r, i, 'first', startMonthDay, endMonthDate),
                },
              ]"
              @click="selectFirstItem(r, i)"
              v-html="getDayCell(r, i, startMonthDay, endMonthDate)"
            />
            <!-- eslint-enable vue/no-v-html -->
          </ul>
        </div>
        <div class="calendar_month_right">
          <div class="months-text">
            <span class="month-title">{{ monthsLocale[startNextActiveMonth] | t }}&nbsp;{{ activeYearEnd }}</span>
            <button :disabled="disableNextMonth" class="is-pulled-right button white-button" @click="goNextMonth">
              <i class="icon-angle-right" aria-hidden="true" />
            </button>
          </div>
          <ul :class="s.daysWeeks">
            <li v-for="item in shortDaysLocale" :key="item">
              {{ item | t }}
            </li>
          </ul>
          <ul v-for="r in 6" :key="r" :class="[s.days]">
            <!-- eslint-disable vue/no-v-html -->
            <li
              v-for="i in numOfDays"
              :key="i"
              :class="[
                {
                  [s.daysSelected]: isDateSelected(r, i, 'second', startNextMonthDay, endNextMonthDate),
                  [s.daysInRange]: isDateInRange(r, i, 'second', startNextMonthDay, endNextMonthDate),
                  [s.dateDisabled]: isDateDisabled(r, i, 'second', startNextMonthDay, endNextMonthDate),
                },
              ]"
              @click="selectSecondItem(r, i)"
              v-html="getDayCell(r, i, startNextMonthDay, endNextMonthDate)"
            />
            <!-- eslint-enable vue/no-v-html -->
          </ul>
        </div>
      </div>
      <div v-if="!showMonth || !isCompact" class="calendar-range">
        <div class="auto-select">
          <button
            v-for="(item, idx) in finalPresetRanges"
            :key="idx"
            class="range-button"
            :class="{ 'active-preset': presetActive === item.label }"
            @click="updatePreset(item)"
          >
            {{ `calendar.${item.label}` | t }}
          </button>
        </div>
        <div class="operation">
          <button class="range-button left" @click="closeCalrendar()">
            {{ "calendar.cancel" | t }}
          </button>
          <button class="range-button right" @click="setDateValue()">
            {{ "calendar.apply" | t }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as dateFns from "date-fns";
import { getEndOfValidDate } from "@/lib/utilFunctions";
import ButtonWithIcon from "./ButtonWithIcon";

const defaultConfig = {};
const defaultI18n = "EN";
const availableMonths = {
  EN: [
    "calendar.january",
    "calendar.february",
    "calendar.march",
    "calendar.april",
    "calendar.may",
    "calendar.june",
    "calendar.july",
    "calendar.august",
    "calendar.september",
    "calendar.october",
    "calendar.november",
    "calendar.december",
  ],
};

const availableShortDays = {
  EN: ["calendar.su", "calendar.mo", "calendar.tu", "calendar.we", "calendar.th", "calendar.fr", "calendar.sa"],
};

const defaultCaptions = {
  title: "Choose Dates",
  ok_button: "Apply",
};

const defaultStyle = {
  daysWeeks: "calendar_weeks",
  days: "calendar_days",
  daysSelected: "calendar_days_selected",
  daysInRange: "calendar_days_in-range",
  firstDate: "calendar_month_left",
  secondDate: "calendar_month_right",
  presetRanges: "calendar_preset-ranges",
  dateDisabled: "calendar_days--disabled",
};

const defaultPresets = function () {
  const n = getEndOfValidDate();

  return {
    // thisOneYear: function () {
    //   const startMonth = dateFns.startOfDay(dateFns.subDays(n, 365))
    //   const endMonth = dateFns.startOfDay(n)
    //   return {
    //     label: 'Last 365 Days',
    //     active: false,
    //     dateRange: {
    //       start: startMonth,
    //       end: endMonth
    //     }
    //   }
    // },
    last30Days: function () {
      const startMonth = dateFns.startOfDay(dateFns.subDays(n, 29));
      const endMonth = dateFns.startOfDay(n);
      return {
        label: "last30Days",
        active: false,
        dateRange: {
          start: startMonth,
          end: endMonth,
        },
      };
    },
    last7days: function () {
      const startMonth = dateFns.startOfDay(dateFns.subDays(n, 6));
      const endMonth = dateFns.startOfDay(n);
      return {
        label: "last7Days",
        active: false,
        dateRange: {
          start: startMonth,
          end: endMonth,
        },
      };
    },
    lastMonth: function () {
      const startMonth = dateFns.addDays(dateFns.startOfMonth(dateFns.subMonths(n, 1)), 1);
      const endMonth = dateFns.startOfMonth(n);
      return {
        label: "lastMonth",
        active: false,
        dateRange: {
          start: startMonth,
          end: endMonth,
        },
      };
    },
    lastWeek: function () {
      const startMonth = dateFns.addDays(dateFns.startOfWeek(dateFns.subWeeks(n, 1)), 1);
      const endMonth = dateFns.startOfWeek(n);
      return {
        label: "lastWeek",
        active: false,
        dateRange: {
          start: startMonth,
          end: endMonth,
        },
      };
    },
  };
};

export default {
  components: {
    ButtonWithIcon,
  },
  props: {
    configs: {
      type: Object,
      default: () => defaultConfig,
    },
    i18n: {
      type: String,
      default: defaultI18n,
    },
    months: {
      type: Array,
      default: () => null,
    },
    shortDays: {
      type: Array,
      default: () => null,
    },
    // options for captions are: title, ok_button
    captions: {
      type: Object,
      default: () => defaultCaptions,
    },
    format: {
      type: String,
      default: "DD MMM YYYY",
    },
    styles: {
      type: Object,
      default: () => {},
    },
    initRange: {
      type: Object,
      default: () => {},
    },
    presetRanges: {
      type: Object,
      default: () => null,
    },
    compact: {
      type: String,
      default: "false",
    },
    righttoleft: {
      type: String,
      default: "false",
    },
  },
  data() {
    return {
      dateRange: {},
      numOfDays: 7,
      isFirstChoice: true,
      isOpen: false,
      presetActive: "",
      showMonth: false,
      activeMonthStart: new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1,
      activeYearStart: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
      activeYearEnd: this.startActiveYear,
    };
  },
  computed: {
    disableNextMonth() {
      const today = new Date();
      return (
        dateFns.differenceInMilliseconds(
          today,
          dateFns.addMonths(new Date(this.activeYearEnd, this.startNextActiveMonth), 1)
        ) < 0
      );
    },
    DateLabel() {
      return `${this.getDateString(this.dateRange.start, 1)} - ${this.getDateString(this.dateRange.end, 1)}`;
    },
    monthsLocale: function () {
      return this.months || availableMonths[this.i18n];
    },
    shortDaysLocale: function () {
      return this.shortDays || availableShortDays[this.i18n];
    },
    s: function () {
      return Object.assign({}, defaultStyle, this.style);
    },
    startMonthDay: function () {
      return new Date(this.activeYearStart, this.activeMonthStart, 1).getDay();
    },
    startNextMonthDay: function () {
      return new Date(this.activeYearEnd, this.startNextActiveMonth, 1).getDay();
    },
    endMonthDate: function () {
      return new Date(this.activeYearEnd, this.startNextActiveMonth, 0).getDate();
    },
    endNextMonthDate: function () {
      return new Date(this.activeYearEnd, this.activeMonthStart + 2, 0).getDate();
    },
    startNextActiveMonth: function () {
      return this.activeMonthStart >= 11 ? 0 : this.activeMonthStart + 1;
    },
    finalPresetRanges: function () {
      const tmp = {};
      const presets = this.presetRanges || defaultPresets(this.i18n);
      for (const i in presets) {
        const item = presets[i];
        let plainItem = item;
        if (typeof item === "function") {
          plainItem = item();
        }
        tmp[i] = plainItem;
      }
      return tmp;
    },
    isCompact: function () {
      return this.compact === "true";
    },
    isRighttoLeft: function () {
      return this.righttoleft === "true";
    },
    isBefore2019() {
      return this.activeYearStart === 2019 && this.activeMonthStart === 0;
    },
  },
  watch: {
    startNextActiveMonth: function (value) {
      if (value === 0) this.activeYearEnd = this.activeYearStart + 1;
    },
  },
  created() {
    if (this.initRange) {
      this.dateRange = this.initRange;
    }
    if (this.activeMonthStart === 11) {
      this.activeYearEnd = this.activeYearStart + 1;
    } else {
      this.activeYearEnd = this.activeYearStart;
    }
    window.addEventListener("click", this.closeCalrendar, false);
  },
  beforeDestroy() {
    window.removeEventListener("click", this.closeCalrendar, false);
  },
  methods: {
    elementClicked(e) {
      e.stopPropagation(); // this is for don't catch event at ourside of this element.
    },
    closeCalrendar: function () {
      this.$emit("closed");
      if (this.initRange) {
        this.dateRange = this.initRange;
      }
      this.isOpen = false;
      this.showMonth = false;
    },
    toggleCalendar: function () {
      this.isOpen = !this.isOpen;
      this.showMonth = !this.showMonth;
    },
    getDateString: function (date) {
      if (!date) {
        return "";
      }
      return dateFns.format(dateFns.subDays(date, 1), "yyyy/MM/dd");
    },
    getDayIndexInMonth: function (r, i, startMonthDay) {
      const date = this.numOfDays * (r - 1) + i;
      return date - startMonthDay;
    },
    getDayCell(r, i, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      return result > 0 && result <= endMonthDate ? result : "&nbsp;";
    },
    getNewDateRange(result, activeMonth, activeYear) {
      const newData = {};
      let key = "start";
      if (!this.isFirstChoice) {
        key = "end";
      } else {
        newData["end"] = null;
      }
      const resultDate = new Date(activeYear, activeMonth, result);
      if (!this.isFirstChoice && resultDate < this.dateRange.start) {
        this.isFirstChoice = false;
        return { start: resultDate };
      }

      // toggle first choice
      this.isFirstChoice = !this.isFirstChoice;
      newData[key] = resultDate;
      return newData;
    },
    selectFirstItem(r, i) {
      const result = this.getDayIndexInMonth(r, i, this.startMonthDay) + 1;
      this.dateRange = Object.assign(
        {},
        this.dateRange,
        this.getNewDateRange(result, this.activeMonthStart, this.activeYearStart)
      );
      if (this.dateRange.start && this.dateRange.end) {
        this.presetActive = "";
        if (this.isCompact) {
          this.showMonth = false;
        }
      }
    },
    selectSecondItem(r, i) {
      const result = this.getDayIndexInMonth(r, i, this.startNextMonthDay) + 1;
      this.dateRange = Object.assign(
        {},
        this.dateRange,
        this.getNewDateRange(result, this.startNextActiveMonth, this.activeYearEnd)
      );
      if (this.dateRange.start && this.dateRange.end) {
        this.presetActive = "";
      }
    },
    getCurrentDate(r, i, key, startMonthDay, endMonthDate, result) {
      let currDate = null;
      if (key === "first") {
        currDate = new Date(this.activeYearStart, this.activeMonthStart, result);
      } else {
        currDate = new Date(this.activeYearEnd, this.startNextActiveMonth, result);
      }
      return currDate;
    },
    isDateSelected(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay) + 1;
      if (result < 2 || result > endMonthDate + 1) return false;

      const currDate = this.getCurrentDate(r, i, key, startMonthDay, endMonthDate, result);
      return (
        (this.dateRange.start && this.dateRange.start.getTime() === currDate.getTime()) ||
        (this.dateRange.end && this.dateRange.end.getTime() === currDate.getTime())
      );
    },
    isDateInRange(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay) + 1;
      if (result < 2 || result > endMonthDate + 1) return false;

      const currDate = this.getCurrentDate(r, i, key, startMonthDay, endMonthDate, result);
      return (
        this.dateRange.start &&
        this.dateRange.start.getTime() < currDate.getTime() &&
        this.dateRange.end &&
        this.dateRange.end.getTime() > currDate.getTime()
      );
    },
    isDateDisabled(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay) + 1;
      if (result < 2 || result > endMonthDate + 1) return true;
      const currDate = this.getCurrentDate(r, i, key, startMonthDay, endMonthDate, result);
      const END_OF_VALID_DATE = getEndOfValidDate();
      return (
        dateFns.differenceInMilliseconds(currDate, END_OF_VALID_DATE) > 0 ||
        (!this.dateRange.end && dateFns.differenceInMilliseconds(currDate, this.dateRange.start) < 0)
      );
    },
    goPrevMonth() {
      const prevMonth = new Date(this.activeYearStart, this.activeMonthStart, 0);
      this.activeMonthStart = prevMonth.getMonth();
      this.activeYearStart = prevMonth.getFullYear();
      this.activeYearEnd = prevMonth.getFullYear();
    },
    goNextMonth() {
      const nextMonth = new Date(this.activeYearEnd, this.startNextActiveMonth, 1);
      this.activeMonthStart = nextMonth.getMonth();
      this.activeYearStart = nextMonth.getFullYear();
      this.activeYearEnd = nextMonth.getFullYear();
    },
    updatePreset(item) {
      this.presetActive = item.label;
      this.dateRange = item.dateRange;
      // update start active month
      const currentMonthNumber = this.dateRange.end.getMonth();
      if (currentMonthNumber === 0) {
        this.activeMonthStart = 11;
        this.activeYearStart = this.dateRange.end.getFullYear() - 1;
      } else {
        this.activeMonthStart = currentMonthNumber - 1;
        this.activeYearStart = this.dateRange.end.getFullYear();
      }
      this.activeYearEnd = this.dateRange.end.getFullYear();
    },
    setDateValue: function () {
      this.$emit("selected", this.dateRange);
      if (!this.isCompact) {
        this.toggleCalendar();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.calendar-body {
  color: #3a3a3a;
}

.input-date {
  width: 100%;
  border: 1px solid #ccc;
  font-size: 14px;

  &.active {
    color: white;
    background-color: $primary;
  }
}

.active-preset {
  border: 1px solid #0096d9;
  border-radius: 3px;
  color: #11988e;
}

.months-text {
  font-weight: bold;
  text-align: center;

  .button {
    width: 16px;
    height: 16px;
    padding: 0;
  }
}

.calendar-root {
  position: relative;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.calendar-title {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.calendar-right-to-left {
  right: 0;
}

.calendar {
  position: absolute;
  z-index: 9;
  display: block;
  width: 550px;
  height: 350px;
  font-size: 12px;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  background: #fff;
  box-shadow: -3px 4px 12px -1px #ccc;
}

.calendar-head h2 {
  margin: 0;
  padding: 20px 0 0 20px;
}

.close {
  &:hover {
    cursor: pointer;
  }

  float: right;
  margin-top: -35px;
  padding: 0 10px;
  font-weight: normal;
  font-size: 32px;
}

.calendar ul {
  list-style-type: none;
}

.calendar-wrap {
  display: inline-block;
  width: 100%;
  padding: 10px;
}

.calendar-range {
  margin: -2px;
  padding: 0 12px;
  border-left: 1px solid #ccc;
}

.calendar-left-mobile {
  width: 100% !important;
}

.calendar_month_left,
.calendar_month_right {
  float: left;
  width: 47%;
  margin: 5px;
  padding: 10px;
}

.calendar_weeks {
  width: auto;
  margin: 0;
  padding: 10px 0;

  li {
    display: inline-block;
    width: 13.6%;
    color: #999;
    text-align: center;
  }
}

.calendar_days {
  margin: 0;
  padding: 0;

  li {
    display: inline-block;
    width: 13.6%;
    color: #333;
    line-height: 2em;
    text-align: center;
    cursor: pointer;
  }
}

.calendar_preset li {
  display: block;
  width: auto;
  line-height: 2.6em;
}

.calendar_days li:hover {
  color: white;
  background: #11988e;
}

li {
  &.calendar_days--disabled {
    color: #c8c8c8;
    pointer-events: none;
  }

  &.calendar_days_selected {
    color: #fff;
    background: #11988e;
  }

  &.calendar_days_in-range {
    color: #fff;
    background: #87dfd9;
  }
}

.calendar_preset {
  padding: 0;
}

.calendar-mobile {
  z-index: 1;
  width: 260px;
  box-shadow: none;
}

.calendar-range-mobile {
  width: 90%;
  margin: -20px 0;
  padding: 2px;
  border-left: none;
}

.range-button {
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  font-size: 10px;
  background: #fff;
  cursor: pointer;

  &:focus {
    outline: none;
  }
}

.auto-select {
  margin-bottom: 20px;

  .range-button {
    width: 80px;
    margin-right: 10px;
  }
}

.operation {
  display: flex;

  .range-button {
    width: 106px;
    font-size: 16px;

    &.right {
      margin-left: auto;
      color: white;
      background-color: #16c6b9;
    }
  }
}
</style>
